import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
];

export default function Settings({ setTitle }) {
  const [form, setForm]     = useState({
    company_name: '', company_address: '', company_phone: '',
    company_email: '', company_website: '',
    currency: '$', currency_code: 'USD',
    tax_rate: '0', financial_year: '01',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    setTitle('Company Settings');
    api.get('/settings').then(r => {
      setForm(prev => ({ ...prev, ...(r.data || {}) }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [setTitle]);

  const f = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setSaved(false);
  };

  const handleCurrencyChange = (code) => {
    const cur = CURRENCIES.find(c => c.code === code);
    if (cur) { f('currency_code', cur.code); f('currency', cur.symbol); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving settings');
    } finally { setSaving(false); }
  };

  const months = [
    'January (01)', 'February (02)', 'March (03)', 'April (04)',
    'May (05)', 'June (06)', 'July (07)', 'August (08)',
    'September (09)', 'October (10)', 'November (11)', 'December (12)',
  ];

  if (loading) return <div className="loading">Loading settings…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Company Settings</h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>

      {saved && <div className="success-msg" style={{ marginBottom: 16 }}>Settings saved successfully.</div>}

      {/* Company Info */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h3>Company Information</h3></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group full">
              <label>Company Name</label>
              <input className="form-control" value={form.company_name} onChange={e => f('company_name', e.target.value)} placeholder="Your Company Name" />
            </div>
            <div className="form-group full">
              <label>Address</label>
              <textarea className="form-control" value={form.company_address} onChange={e => f('company_address', e.target.value)} placeholder="Company address" />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input className="form-control" value={form.company_phone} onChange={e => f('company_phone', e.target.value)} placeholder="+1 234 567 8900" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" className="form-control" value={form.company_email} onChange={e => f('company_email', e.target.value)} placeholder="info@company.com" />
            </div>
            <div className="form-group full">
              <label>Website</label>
              <input className="form-control" value={form.company_website} onChange={e => f('company_website', e.target.value)} placeholder="https://www.company.com" />
            </div>
          </div>
        </div>
      </div>

      {/* Financial Settings */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h3>Financial Settings</h3></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group">
              <label>Currency</label>
              <select className="form-control" value={form.currency_code} onChange={e => handleCurrencyChange(e.target.value)}>
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol} — {c.name} ({c.code})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Currency Symbol</label>
              <input className="form-control" value={form.currency} onChange={e => f('currency', e.target.value)} placeholder="$" />
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Auto-filled when selecting currency above</span>
            </div>
            <div className="form-group">
              <label>Tax Rate (%)</label>
              <input type="number" className="form-control" value={form.tax_rate} onChange={e => f('tax_rate', e.target.value)} placeholder="0" step="0.01" min="0" max="100" />
            </div>
            <div className="form-group">
              <label>Financial Year Start Month</label>
              <select className="form-control" value={form.financial_year} onChange={e => f('financial_year', e.target.value)}>
                {months.map((m, i) => {
                  const val = String(i + 1).padStart(2, '0');
                  return <option key={val} value={val}>{m}</option>;
                })}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="card">
        <div className="card-header"><h3>Preview</h3></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group">
              <label>Company Name</label>
              <div><strong>{form.company_name || '—'}</strong></div>
            </div>
            <div className="form-group">
              <label>Currency Display</label>
              <div><strong>{form.currency}{(1234.50).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></div>
            </div>
            <div className="form-group">
              <label>Tax Rate</label>
              <div>{form.tax_rate}%</div>
            </div>
            <div className="form-group">
              <label>Financial Year</label>
              <div>Starts {months[parseInt(form.financial_year, 10) - 1]?.split(' ')[0] || '—'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
